import { useMemo, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { WorkAreaManagePolicyDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { default as ToggleSelector } from 'modules/core/components/ToggleSelector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FormProvider } from 'modules/core/hooks/useForm'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useLocations } from 'modules/locations/hooks/useLocations'
import { useWorkAreas } from 'modules/locations/hooks/useWorkAreas'
import WorkSpaceFilter from 'modules/space_control/components/WorkSpaceFilter'
import WorkSpacesList from 'modules/space_control/components/WorkSpacesList'
import { WorkSpacesMap } from 'modules/space_control/components/WorkSpacesMap'
import LoadingMap from 'modules/space_control/components/WorkSpacesMap/LoadingState'
import { CARD_MODE } from 'modules/space_control/components/WorkSpacesMap/types'
import { DateBookingProvider } from 'modules/space_control/components/dateBookingContext'
import { useWorkAreaMap } from 'modules/space_control/hooks/useWorkAreaMap'
import { WorkAreaMapNodes, useWorkAreaMaps } from 'modules/space_control/hooks/useWorkAreaMaps'
import { useUserMapEvents } from 'modules/space_control/hooks/workSpacesMap/useMapEvents'

const MODE = {
  CARDS: 'cards',
  MAP: 'map',
}

interface CardsViewProps {
  selectedWorkAreaId?: string
  selectedLocationId?: string
  variables: {
    startTime: string
    endTime: string
    locationId: string
    workAreaId: string
    date: string
    capacity: string
    searchQuery: string
  }
  refetchTrigger?: number
}

const CardsView = ({
  selectedWorkAreaId,
  selectedLocationId,
  variables,
  refetchTrigger,
}: CardsViewProps) => {
  const capacityNum = variables.capacity ? parseInt(variables.capacity, 10) : null
  const hasFilters = capacityNum || variables.searchQuery
  const query = {
    startTime: variables.startTime,
    endTime: variables.endTime,
    date: i18nDateToString(moment(variables.date), 'backend'),
    locationIds: variables.locationId ? [Number(variables.locationId)] : [],
    workAreaIds: variables.workAreaId ? [Number(variables.workAreaId)] : [],
    sortOrder: { field: 'workAreaName', order: 'asc' },
    filter: hasFilters
      ? {
          ...(capacityNum ? { originalCapacity: { gteq: capacityNum } } : {}),
          ...(variables.searchQuery ? { workSpaceName: { contains: variables.searchQuery } } : {}),
        }
      : undefined,
  }
  return (
    <WorkSpacesList
      key={refetchTrigger}
      selectedWorkAreaId={selectedWorkAreaId}
      selectedLocationId={selectedLocationId}
      queryVariables={query}
    />
  )
}

interface MapViewProps {
  workAreaId: string
  workAreaMap: WorkAreaMapNodes | undefined | null
  variables: {
    startTime: string
    endTime: string
    locationId: string
    workAreaId: string
    date: string
    capacity: string
    searchQuery: string
  }
}

const MapView = ({ workAreaId, workAreaMap, variables }: MapViewProps) => {
  const date = useMemo(() => {
    return i18nDateToString(moment(variables.date), 'backend')
  }, [variables.date])

  const { workAreaMapLocations, loading } = useWorkAreaMap({
    id: workAreaMap?.id || null,
    date: date,
    endTime: variables.endTime,
    startTime: variables.startTime,
  })

  // Apply client-side filtering for capacity and search query
  const filteredWorkAreaMapLocations = useMemo(() => {
    if (!workAreaMapLocations) return []

    let filtered = workAreaMapLocations

    // Apply capacity filter
    if (variables.capacity) {
      const capacityNum = parseInt(variables.capacity, 10)
      if (!isNaN(capacityNum)) {
        filtered = filtered.filter(
          (location) => location.workSpace && location.workSpace.capacity >= capacityNum
        )
      }
    }

    // Apply search query filter
    if (variables.searchQuery) {
      const query = variables.searchQuery.toLowerCase()
      filtered = filtered.filter(
        (location) => location.workSpace && location.workSpace.name.toLowerCase().includes(query)
      )
    }

    return filtered
  }, [workAreaMapLocations, variables.capacity, variables.searchQuery])

  const { actions } = useUserMapEvents({
    workSpaceLocations: filteredWorkAreaMapLocations || [],
  })

  if (loading) return <LoadingMap />
  if (!workAreaMap) return null

  return (
    <WorkSpacesMap
      workAreaId={workAreaId}
      date={date}
      endTime={variables.endTime}
      startTime={variables.startTime}
      workAreaMapLocations={filteredWorkAreaMapLocations || []}
      actions={actions}
      map={workAreaMap}
      cardMode={CARD_MODE.BOOKING}
    />
  )
}

const OverviewPage = () => {
  const location = useLocation()
  const { locations } = useLocations()
  const { workAreas, loading: workAreasLoading } = useWorkAreas({
    locationIds: locations.map((loc) => loc.id),
    onlyNonArchived: true,
  })
  const { employee } = useCurrentSession()
  const [viewMode, setViewMode] = useState(MODE.CARDS)
  const [refetchTrigger, setRefetchTrigger] = useState(0)
  const [userViewModePreference, setUserViewModePreference] = useLocalStorage<
    (typeof MODE)[keyof typeof MODE] | null
  >(LOCAL_STORAGE_KEYS.spaceControlViewModePreference, null)
  useEffect(() => {
    track(trackingEvents.spaceControl.seeOverview)
  }, [])

  // Refetch workspaces when returning from successful booking
  useEffect(() => {
    const shouldRefetch = location.state?.shouldRefetch

    if (shouldRefetch && viewMode === MODE.CARDS) {
      setRefetchTrigger((prev) => prev + 1)
      window.history.replaceState({}, document.title)
    }
  }, [location.state, viewMode])

  const userLocationId = employee && employee.locationId ? employee.locationId.toString() : ''
  const userWorkAreas = workAreas.filter((workArea) => {
    return (
      workArea.location?.id && employee?.locationId && workArea.location.id === employee.locationId
    )
  })
  const firstUserWorkAreaId = userWorkAreas.length > 0 ? userWorkAreas[0]?.id.toString() : ''

  const defaultValues = useUnsafeMemo(() => {
    const now = moment()
    const defaultStartTime = now.add(1, 'hour').startOf('hour').format('HH:mm')
    const defaultEndTime = now.add(1, 'hour').format('HH:mm')

    return {
      startTime: defaultStartTime,
      endTime: defaultEndTime,
      locationId: firstUserWorkAreaId ? userLocationId : '',
      workAreaId: '',
      date: i18nDateToString(moment(), 'backend'),
      capacity: '',
      searchQuery: '',
    }
  }, [userLocationId, firstUserWorkAreaId])

  const [variables, setVariables] = useState<{
    startTime: string
    endTime: string
    locationId: string
    workAreaId: string
    date: string
    capacity: string
    searchQuery: string
  }>(defaultValues)

  useEffect(() => {
    if (defaultValues.locationId) {
      setVariables(defaultValues)
    }
  }, [defaultValues.locationId, defaultValues.startTime, defaultValues.endTime, defaultValues.date])

  const { workAreaMapsById, loading: workAreaMapsLoading } = useWorkAreaMaps({
    workAreaIds: variables.workAreaId ? [+variables.workAreaId] : [],
    skip: !variables.workAreaId,
  })

  const { data } = useQuery(WorkAreaManagePolicyDocument)
  const canManageWorkArea = data?.locations?._policies?.workAreaManage?.can ?? false
  const selectedWorkAreaMap = variables.workAreaId ? workAreaMapsById[+variables.workAreaId] : null
  const hasMap = workAreaMapsLoading || selectedWorkAreaMap?.image?.url
  const canSeeMap = variables.workAreaId && variables.workAreaId !== ''
  const shouldDisableMapView = !hasMap && !canManageWorkArea

  useEffect(() => {
    if (shouldDisableMapView || !canSeeMap || !hasMap) {
      setViewMode(MODE.CARDS)
    } else if (userViewModePreference === MODE.MAP) {
      setViewMode(MODE.MAP)
    } else {
      setViewMode(MODE.CARDS)
    }
  }, [
    variables.workAreaId,
    shouldDisableMapView,
    hasMap,
    canSeeMap,
    variables.date,
    variables.endTime,
    variables.startTime,
    variables.locationId,
    userViewModePreference,
  ])

  const handleViewModeChange = (value: string) => {
    setViewMode(value)
    setUserViewModePreference(value)
  }

  if (workAreasLoading) return null

  return (
    <ResponsivePage.Body showHeader={false}>
      <FormProvider
        defaultValues={defaultValues}
        onSubmit={(values) => {
          setVariables({
            ...(values.locationId ? { locationId: values.locationId } : { locationId: '' }),
            ...(values.startTime ? { startTime: values.startTime } : { startTime: '' }),
            ...(values.endTime ? { endTime: values.endTime } : { endTime: '' }),
            ...(values.workAreaId ? { workAreaId: values.workAreaId } : { workAreaId: '' }),
            date: values.date,
            capacity: values.capacity || '',
            searchQuery: values.searchQuery || '',
          })

          return Promise.resolve({ success: true, data: '' })
        }}
      >
        <>
          <DateBookingProvider
            value={{
              date: moment(variables.date),
            }}
          >
            <Box flexGrow flexDirection="row" gap="s24" alignItems="flexStart">
              <Box gap="s24">
                <Box gap="s8" flexDirection="row" alignItems="flexEnd">
                  <Text.Header2>
                    {variables.workAreaId
                      ? `${
                          workAreas.find((wa) => wa.id.toString() === variables.workAreaId)
                            ?.location?.name
                        }, ${
                          workAreas.find((wa) => wa.id.toString() === variables.workAreaId)?.name
                        }`
                      : i18n.t('space_control.overview.filters.all_workareas')}
                  </Text.Header2>
                  {variables.workAreaId && (
                    <Text.Normal weight="medium">
                      {i18n.tp('space_control.overview.spaces_count', {
                        count:
                          workAreas.find((wa) => wa.id.toString() === variables.workAreaId)
                            ?.workSpacesConnection?.totalCount ?? 0,
                      })}
                    </Text.Normal>
                  )}
                </Box>
                <WorkSpaceFilter locations={locations} workAreas={workAreas} />
              </Box>
              <Box flexGrow flexDirection="column" gap="s24">
                {canSeeMap && (
                  <Box flexDirection="row" justifyContent="flexEnd">
                    <ToggleSelector
                      value={viewMode}
                      options={[
                        {
                          icon: 'more-options',
                          label: i18n.t('space_control.overview.toggle.cards'),
                          value: MODE.CARDS,
                        },
                        {
                          icon: 'pin',
                          label: hasMap
                            ? i18n.t('space_control.overview.toggle.map')
                            : i18n.t('space_control.overview.no_map_configured'),
                          value: MODE.MAP,
                          disabled: shouldDisableMapView,
                        },
                      ]}
                      onChange={handleViewModeChange}
                    />
                  </Box>
                )}
                {viewMode === MODE.CARDS ? (
                  <CardsView
                    selectedWorkAreaId={variables.workAreaId}
                    selectedLocationId={variables.locationId}
                    variables={variables}
                    refetchTrigger={refetchTrigger}
                  />
                ) : (
                  <MapView
                    workAreaId={variables.workAreaId}
                    workAreaMap={selectedWorkAreaMap}
                    variables={variables}
                  />
                )}
              </Box>
            </Box>
            <Outlet />
          </DateBookingProvider>
        </>
      </FormProvider>
    </ResponsivePage.Body>
  )
}

export default OverviewPage
